/**
 * Bulk attach (KPR-187): which item each dropped file belongs to.
 *
 * Pure. The panel hands it the files and the items in scope, each as
 * `{ id, title, values: { [elementName]: value }, externalId }`, and a rule:
 *
 *   { kind: 'title' }                        the file name contains the item's title
 *   { kind: 'element', element: '<name>' }   the file name IS an element's value
 *   { kind: 'externalId' }                   the file name IS the import id
 *   { kind: 'request', entries }             an upload request's own entries (KPR-183)
 *
 * Names and titles are compared NORMALISED: case and accents folded, the
 * extension dropped, and every run of separators or punctuation read as one
 * space — "INV_2024-031.pdf" and "Inv 2024 031" are the same words.
 *
 * The title rule matches on WORD BOUNDARIES, never as a raw substring: a
 * title is found in a name only where it starts and ends at a word edge.
 * "Receipt 1" is therefore not in "Receipt 12.jpg", which a substring test
 * would have made ambiguous for every receipt past nine. When the name
 * equals a title outright, that item wins. When several titles are found
 * and every one of them sits inside the longest ("oak" inside "oak table"),
 * the longest wins; any other overlap is ambiguous and the person decides.
 * A title shorter than MIN_TITLE characters never matches by containment —
 * a one-letter title would claim every file.
 */

export const MIN_TITLE = 2;

/** Lower case, accents folded, separators and punctuation as single spaces. */
export function normalise(text) {
	return `${text ?? ''}`
		.normalize('NFKD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();
}

/** The file name without its extension (a short trailing ".ext" only). */
export function stemOf(name) {
	const text = `${name ?? ''}`;
	const dot = text.lastIndexOf('.');
	if (dot > 0 && text.length - dot - 1 <= 5 && /^[a-z0-9]+$/i.test(text.slice(dot + 1))) return text.slice(0, dot);
	return text;
}

const bounded = (haystack, needle) => ` ${haystack} `.includes(` ${needle} `);

function valueText(value) {
	if (value === null || value === undefined) return '';
	if (typeof value === 'number' || typeof value === 'string') return `${value}`;
	return '';
}

/** One file against the items: `{ status, itemId, candidateIds }`. */
function matchOne(stem, items, rule) {
	if (!stem) return { status: 'unmatched', itemId: null, candidateIds: [] };

	if (rule.kind === 'title') {
		const exact = items.filter(it => it.norm === stem);
		if (exact.length === 1) return { status: 'matched', itemId: exact[0].id, candidateIds: [exact[0].id] };
		if (exact.length > 1) return { status: 'ambiguous', itemId: null, candidateIds: exact.map(it => it.id) };

		const found = items.filter(it => it.norm.length >= MIN_TITLE && bounded(stem, it.norm));
		if (found.length === 1) return { status: 'matched', itemId: found[0].id, candidateIds: [found[0].id] };
		if (found.length > 1) {
			const longest = found.reduce((a, b) => (b.norm.length > a.norm.length ? b : a));
			const sameTitle = found.filter(it => it.norm === longest.norm);
			const nested = found.every(it => it.norm === longest.norm || bounded(longest.norm, it.norm));
			if (nested && sameTitle.length === 1) return { status: 'matched', itemId: longest.id, candidateIds: [longest.id] };
			return { status: 'ambiguous', itemId: null, candidateIds: found.map(it => it.id) };
		}
		return { status: 'unmatched', itemId: null, candidateIds: [] };
	}

	const hits = items.filter(it => it.key && it.key === stem);
	if (hits.length === 1) return { status: 'matched', itemId: hits[0].id, candidateIds: [hits[0].id] };
	if (hits.length > 1) return { status: 'ambiguous', itemId: null, candidateIds: hits.map(it => it.id) };
	return { status: 'unmatched', itemId: null, candidateIds: [] };
}

/**
 * Match every file. `files` are `{ key, name }`. Returns
 * `{ byKey: { [key]: { status, itemId, candidateIds } }, itemsWithoutFile: [id] }`.
 */
export function matchFiles(files, items, rule = { kind: 'title' }) {
	const prepared = (items || []).map(it => ({
		id: it.id,
		norm: normalise(it.title),
		key: rule.kind === 'element' ? normalise(valueText(it.values?.[rule.element]))
			: rule.kind === 'externalId' ? normalise(it.externalId) : '',
	}));
	const byKey = {};
	const used = new Set();
	for (const file of files || []) {
		const result = matchOne(normalise(stemOf(file.name)), prepared, rule);
		byKey[file.key] = result;
		if (result.itemId) used.add(result.itemId);
	}
	const itemsWithoutFile = prepared.filter(it => !used.has(it.id)).map(it => it.id);
	return { byKey, itemsWithoutFile };
}

/**
 * `*` and `?` only, the whole name, case-insensitive — the API's own rule and
 * algorithm (uploadRequestUtils `globMatches`). A linear two-pointer match,
 * never a RegExp: `*a*a*a*a*b` against a long name backtracks for seconds (the
 * KPR-183 review's ReDoS; here it would only hang this tab, but a pattern
 * comes from someone else's agent).
 */
export function globMatches(pattern, name) {
	// NFC first: macOS hands the browser decomposed names (Cafe + U+0301), an
	// agent writes composed ones; the API folds the same way.
	const p = `${pattern ?? ''}`.normalize('NFC').toLowerCase();
	const n = `${name ?? ''}`.normalize('NFC').toLowerCase();
	let pi = 0; let ni = 0; let star = -1; let mark = 0;
	while (ni < n.length) {
		if (pi < p.length && (p[pi] === '?' || (p[pi] !== '*' && p[pi] === n[ni]))) { pi += 1; ni += 1; }
		else if (pi < p.length && p[pi] === '*') { star = pi; mark = ni; pi += 1; }
		else if (star !== -1) { pi = star + 1; mark += 1; ni = mark; }
		else return false;
	}
	while (pi < p.length && p[pi] === '*') pi += 1;
	return pi === p.length;
}

/**
 * The upload request rule (KPR-183): each file against the request's
 * entries `{ itemId, name, pattern, element, fulfilled }`. An entry already
 * fulfilled takes nothing more. Three passes, most specific first — the
 * order the API counts fulfilment in — so a `*.pdf` entry never takes the
 * `R-1.pdf` another entry names:
 *   1. entries with a `name` (equal, case-insensitive);
 *   2. entries with a `pattern`, for the files pass 1 left;
 *   3. entries with neither ("any file" for their item), matched by the
 *      title rule among their own items, for the files still left.
 * In passes 1 and 2 a file that open entries of several items claim is
 * ambiguous; of one item, it takes the first such entry, so that entry's
 * `element` is its destination. One file per entry.
 * Returns per file `{ status, itemId, candidateIds, entryIndex }`.
 */
export function matchRequest(files, entries, items) {
	const open = (entries || []).map((e, index) => ({ ...e, index })).filter(e => !e.fulfilled);
	const byName = open.filter(e => e.name);
	const byPattern = open.filter(e => !e.name && e.pattern);
	const loose = open.filter(e => !e.name && !e.pattern);
	const looseItems = (items || []).filter(it => loose.some(e => e.itemId === it.id));
	const list = files || [];
	const byKey = {};
	const taken = new Set();

	const claimPass = (tier, claimsFile) => {
		for (const file of list) {
			if (byKey[file.key]) continue; // matched, or ambiguous at a more specific tier
			const fileName = `${file.name ?? ''}`;
			const claims = tier.filter(e => !taken.has(e.index) && claimsFile(e, fileName));
			const claimItems = [...new Set(claims.map(e => e.itemId))];
			if (claimItems.length === 1) {
				taken.add(claims[0].index);
				byKey[file.key] = { status: 'matched', itemId: claimItems[0], candidateIds: claimItems, entryIndex: claims[0].index };
			} else if (claimItems.length > 1) {
				byKey[file.key] = { status: 'ambiguous', itemId: null, candidateIds: claimItems, entryIndex: null };
			}
		}
	};
	const fold = v => `${v}`.normalize('NFC').toLowerCase();
	claimPass(byName, (e, fileName) => fold(e.name) === fold(fileName));
	claimPass(byPattern, (e, fileName) => globMatches(e.pattern, fileName));

	for (const file of list) {
		if (byKey[file.key]) continue;
		const r = matchFiles([file], looseItems, { kind: 'title' }).byKey[file.key];
		const entry = r.itemId ? loose.find(e => e.itemId === r.itemId && !taken.has(e.index)) : null;
		if (entry) taken.add(entry.index);
		byKey[file.key] = { ...r, entryIndex: entry ? entry.index : null };
	}
	const used = new Set(Object.values(byKey).map(r => r.itemId).filter(Boolean));
	const itemsWithoutFile = [...new Set(open.map(e => e.itemId))].filter(id => !used.has(id));
	return { byKey, itemsWithoutFile };
}

/** Which rules a set of items can be matched by: element keys that hold text or numbers, and the import id when any item has one. */
export function availableRules(items, elementsForCard) {
	const elementChoices = new Map();
	for (const item of items || []) {
		for (const el of elementsForCard(item.cardId) || []) {
			if (!['text-small', 'number'].includes(el?.dataType)) continue;
			if (!elementChoices.has(el.name)) elementChoices.set(el.name, el);
		}
	}
	const hasExternalId = (items || []).some(it => typeof it.externalId === 'string' && it.externalId.trim());
	return { elements: [...elementChoices.values()], externalId: hasExternalId };
}
